import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../Toast';
import { findDuplicateStudentGroups, isMaskedStudentName, mergeDuplicateStudents } from '../../../utils/batchStudentMerge';
import { trackSaenggibuUxEvent } from '../../../utils/eventTracker';
import './BatchDuplicateStudentCard.css';

const COLLAPSED_CHIP_COUNT = 12;

export const getMergedMetaItems = (student) => (
  student?.merged_count > 1 ? [`자료 ${student.merged_count}개 합침`] : []
);

const BatchDuplicateStudentCard = ({ students, onStudentsChange, category }) => {
  const { showToast } = useToast();
  const allGroups = useMemo(() => findDuplicateStudentGroups(students), [students]);
  // 따로 두기로 한 이름은 목록이 바뀌어도 다시 묻지 않는다.
  const [keptKeys, setKeptKeys] = useState(() => new Set());
  // 기본 선택(가린 이름만 빠짐)에서 선생님이 눌러 뒤집은 이름
  const [toggledKeys, setToggledKeys] = useState(() => new Set());
  const [showAllChips, setShowAllChips] = useState(false);
  const groups = useMemo(() => allGroups.filter((group) => !keptKeys.has(group.key)), [allGroups, keptKeys]);
  const isMulti = groups.length > 1;
  const isSelected = (group) => isMaskedStudentName(group.name) === toggledKeys.has(group.key);
  // 한 명뿐이면 칩 없이 합치기·따로 두기 버튼으로 바로 고른다.
  const selectedGroups = isMulti ? groups.filter(isSelected) : groups;
  const maskedCount = groups.filter((group) => isMaskedStudentName(group.name)).length;
  const signature = groups.map((group) => `${group.key}:${group.indexes.length}`).join('|');
  const extraCount = groups.reduce((sum, group) => sum + group.indexes.length - 1, 0);
  const trackedSignatureRef = useRef('');

  const trackDuplicate = useCallback((stage, itemCount) => {
    trackSaenggibuUxEvent('saenggibu_batch_merge_same_student', {
      category,
      is_batch: true,
      stage,
      found_count: groups.length,
      over_count: extraCount,
      item_count: itemCount,
      student_count: students.length,
    });
  }, [category, extraCount, groups.length, students.length]);

  useEffect(() => {
    if (groups.length === 0 || trackedSignatureRef.current === signature) return;
    trackedSignatureRef.current = signature;
    trackDuplicate('shown', groups.length);
  }, [groups.length, signature, trackDuplicate]);

  const toggleGroup = useCallback((key) => {
    setToggledKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleMerge = useCallback(() => {
    if (selectedGroups.length === 0) return;
    const beforeStudents = students;
    const beforeKept = keptKeys;
    const mergeKeys = new Set(selectedGroups.map((group) => group.key));
    const skippedKeys = groups.filter((group) => !mergeKeys.has(group.key)).map((group) => group.key);
    onStudentsChange(mergeDuplicateStudents(beforeStudents, mergeKeys));
    setKeptKeys(new Set([...beforeKept, ...skippedKeys]));
    setToggledKeys(new Set());
    trackDuplicate('merge', mergeKeys.size);
    showToast(`학생 ${mergeKeys.size}명의 나뉜 자료를 하나로 합쳤어요`, 'success', 6000, {
      label: '실행 취소',
      onClick: () => {
        onStudentsChange(beforeStudents);
        setKeptKeys(beforeKept);
        trackDuplicate('undo', mergeKeys.size);
      },
    });
  }, [groups, keptKeys, onStudentsChange, selectedGroups, showToast, students, trackDuplicate]);

  const handleKeepSeparate = useCallback(() => {
    setKeptKeys((prev) => new Set([...prev, ...groups.map((group) => group.key)]));
    setToggledKeys(new Set());
    trackDuplicate('keep', 0);
  }, [groups, trackDuplicate]);

  if (groups.length === 0) return null;

  const visibleGroups = showAllChips ? groups : groups.slice(0, COLLAPSED_CHIP_COUNT);
  const hiddenCount = groups.length - visibleGroups.length;
  const maskedNote = isMulti
    ? ` 이름 일부를 가린 학생 ${maskedCount}명은 다른 학생일 수 있어 빼 두었어요.`
    : ' 이름 일부를 가린 학생이라 다른 학생일 수 있어요.';

  return (
    <div className="saenggibu-batch-duplicate-card" data-testid="batch-duplicate-card">
      <div className="saenggibu-batch-duplicate-text">
        <strong>같은 이름이 두 번 이상 나온 학생이 {groups.length}명 있어요</strong>
        <span>
          {isMulti
            ? '한 학생 자료가 나뉘어 들어왔다면 합쳐야 생기부가 하나만 만들어져요. 동명이인은 눌러서 빼 주세요.'
            : `${groups[0].name} ${groups[0].indexes.length}번. 한 학생 자료가 나뉘어 들어왔다면 합쳐야 생기부가 하나만 만들어져요.`}
          {maskedCount > 0 && maskedNote}
        </span>
      </div>
      {isMulti && (
        <div className="saenggibu-batch-duplicate-chips" role="group" aria-label="합칠 학생 고르기">
          {visibleGroups.map((group) => {
            const selected = isSelected(group);
            return (
              <button
                key={group.key}
                type="button"
                className={`saenggibu-batch-duplicate-chip${selected ? ' is-selected' : ''}`}
                aria-pressed={selected}
                onClick={() => toggleGroup(group.key)}
              >
                <span aria-hidden="true">{selected ? '✓' : '+'}</span>
                {group.name} {group.indexes.length}번
              </button>
            );
          })}
          {hiddenCount > 0 && (
            <button type="button" className="saenggibu-batch-duplicate-more" onClick={() => setShowAllChips(true)}>
              {hiddenCount}명 더 보기
            </button>
          )}
        </div>
      )}
      <div className="saenggibu-batch-duplicate-actions">
        <button
          type="button"
          className="saenggibu-batch-duplicate-merge"
          onClick={handleMerge}
          disabled={selectedGroups.length === 0}
        >
          {isMulti ? `고른 ${selectedGroups.length}명 합치기` : '한 학생으로 합치기'}
        </button>
        <button type="button" className="saenggibu-batch-duplicate-keep" onClick={handleKeepSeparate}>
          {isMulti ? '모두 따로 두기' : '동명이인이라 따로 두기'}
        </button>
      </div>
    </div>
  );
};

export default BatchDuplicateStudentCard;
